import { useEffect, useMemo, useState } from "react";
import { Sparkles, X } from "lucide-react";
import {
  fetchReportPrompts,
  suggestPromptValues,
  type ReportPromptDefinition,
} from "@/lib/promptsApi";

type LoadState =
  | { kind: "loading" }
  | { kind: "loaded"; prompts: ReportPromptDefinition[] }
  | { kind: "empty" }
  | { kind: "error"; code: string; detail: string };

interface Props {
  searchPath: string;
  initialValues?: Record<string, string | string[]>;
  onClose: () => void;
  onSubmit: (values: Record<string, string | string[]>) => void;
}

/**
 * "Re-run with filters" modal — Feature #2. Discovers the report's
 * prompt parameters via `/cognos/report/prompts`, renders one input
 * per prompt, lets the user optionally type a natural-language hint
 * and click "Suggest values" to have the LLM populate the form.
 *
 * Submit re-runs the report with the entered parameter values; the
 * resulting chart bubble carries the new sourceRef so subsequent
 * refinements stack iteratively.
 */
export function FiltersModal({ searchPath, initialValues, onClose, onSubmit }: Props) {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [values, setValues] = useState<Record<string, string | string[]>>(initialValues ?? {});
  const [hint, setHint] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const [rationale, setRationale] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchReportPrompts(searchPath)
      .then((prompts) => {
        if (cancelled) return;
        if (prompts.length === 0) { setState({ kind: "empty" }); return; }
        setState({ kind: "loaded", prompts });
        if (!initialValues) {
          const seed: Record<string, string | string[]> = {};
          for (const p of prompts) {
            const src = p.currentValues.length > 0 ? p.currentValues : p.defaultValues;
            if (src.length === 0) continue;
            seed[p.name] = p.multiValued ? [...src] : src[0];
          }
          if (Object.keys(seed).length > 0) setValues(seed);
        }
      })
      .catch((e: { code?: string; detail?: string; message?: string }) => {
        if (cancelled) return;
        setState({
          kind:   "error",
          code:   e.code   ?? "unknown",
          detail: e.detail ?? e.message ?? "Filters unavailable.",
        });
      });
    return () => { cancelled = true; };
  }, [searchPath, initialValues]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const setValue = (name: string, v: string | string[]) => {
    setValues((prev) => ({ ...prev, [name]: v }));
  };

  const onSuggest = async () => {
    if (state.kind !== "loaded" || !hint.trim() || suggesting) return;
    setSuggesting(true);
    setSuggestError(null);
    try {
      const result = await suggestPromptValues(searchPath, hint.trim(), state.prompts);
      setValues((prev) => ({ ...prev, ...result.values }));
      setRationale(result.rationale || null);
      if (result.unmapped.length > 0) {
        setSuggestError(`Couldn't infer values for: ${result.unmapped.join(", ")}. Fill those in manually.`);
      }
    } catch (e: unknown) {
      const err = e as { detail?: string; message?: string };
      setSuggestError(err.detail ?? err.message ?? "Suggestion failed.");
    } finally {
      setSuggesting(false);
    }
  };

  const onApply = () => {
    const cleaned: Record<string, string | string[]> = {};
    for (const [name, v] of Object.entries(values)) {
      if (Array.isArray(v)) {
        const arr = v.map((s) => s.trim()).filter((s) => s.length > 0);
        if (arr.length > 0) cleaned[name] = arr;
      } else if (typeof v === "string" && v.trim().length > 0) {
        cleaned[name] = v.trim();
      }
    }
    onSubmit(cleaned);
  };

  const requiredMissing = useMemo(() => {
    if (state.kind !== "loaded") return [] as string[];
    return state.prompts
      .filter((p) => p.required)
      .filter((p) => {
        const v = values[p.name];
        if (Array.isArray(v)) return v.length === 0;
        return !v || (typeof v === "string" && v.trim().length === 0);
      })
      .map((p) => p.label || p.name);
  }, [state, values]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Re-run with filters"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 p-6 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex w-full max-w-xl max-h-[88vh] flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl"
      >
        <header className="flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-5 py-3">
          <h3 className="m-0 text-sm font-semibold text-slate-900">Re-run with filters</h3>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-500 hover:bg-slate-200"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {state.kind === "loading" && (
          <div className="px-6 py-7 text-center text-sm text-slate-500">Loading the report's filters…</div>
        )}

        {state.kind === "error" && (
          <div role="alert" className="border-t border-rose-100 bg-rose-50 px-5 py-4 text-sm text-rose-900">
            <div className="font-semibold">Filters unavailable.</div>
            <p className="mt-1">{state.detail}</p>
            {state.code === "cognos.sdk.disabled" && (
              <p className="mt-1 text-rose-700">Ask an administrator to enable <code>datumcraft.cognos.sdk.enabled</code>.</p>
            )}
          </div>
        )}

        {state.kind === "empty" && (
          <div className="px-6 py-7 text-center text-sm text-slate-500">
            This report has no prompt parameters — there's nothing to filter on.
          </div>
        )}

        {state.kind === "loaded" && (
          <>
            <div className="border-b border-dashed border-slate-200 px-5 py-3">
              <label htmlFor="filters-nl" className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Describe what you want (optional)
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="filters-nl"
                  type="text"
                  className="h-8 flex-1 min-w-0 rounded-md border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 focus:border-indigo-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100"
                  placeholder="e.g. Italy 2024, only Q4 last year"
                  value={hint}
                  onChange={(e) => setHint(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void onSuggest(); } }}
                  disabled={suggesting}
                  aria-label="Natural-language filter hint"
                />
                <button
                  type="button"
                  onClick={() => { void onSuggest(); }}
                  disabled={suggesting || hint.trim().length === 0}
                  className="inline-flex h-8 items-center gap-1 rounded-md border border-slate-200 bg-amber-50 px-3 text-xs font-medium text-slate-900 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                  {suggesting ? "Suggesting…" : "Suggest values"}
                </button>
              </div>
              {rationale && <p className="mt-2 text-xs italic text-slate-500">{rationale}</p>}
              {suggestError && <p role="alert" className="mt-2 text-xs text-amber-700">{suggestError}</p>}
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3">
              {state.prompts.map((p) => (
                <PromptField
                  key={p.name}
                  prompt={p}
                  value={values[p.name]}
                  onChange={(v) => setValue(p.name, v)}
                />
              ))}
            </div>
          </>
        )}

        <footer className="flex items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-5 py-3">
          {state.kind === "loaded" && requiredMissing.length > 0 ? (
            <span className="flex-1 text-xs text-amber-700">Missing: {requiredMissing.join(", ")}</span>
          ) : <span className="flex-1" />}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-8 rounded-md border border-slate-200 bg-transparent px-4 text-sm text-slate-700 hover:bg-slate-100"
            >
              Cancel
            </button>
            {state.kind === "loaded" && (
              <button
                type="button"
                onClick={onApply}
                disabled={requiredMissing.length > 0}
                className="h-8 rounded-md bg-gradient-to-br from-indigo-500 to-blue-600 px-4 text-sm font-medium text-white hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Re-run report
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}

function PromptField({
  prompt, value, onChange,
}: {
  prompt:   ReportPromptDefinition;
  value:    string | string[] | undefined;
  onChange: (v: string | string[]) => void;
}) {
  const label = (prompt.label && prompt.label.length > 0) ? prompt.label : prompt.name;
  const inputType = inputTypeFor(prompt);
  const hint = `${prompt.dataType.replace(/^xsd/, "").toLowerCase()}${prompt.multiValued ? " (multi)" : ""}`;
  return (
    <div className="flex flex-col gap-1">
      <label className="flex items-baseline gap-2 text-xs text-slate-600">
        <span>{label}</span>
        {prompt.required && <span className="font-semibold text-rose-600" aria-hidden="true">*</span>}
        <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-400">{hint}</span>
      </label>
      {prompt.multiValued ? (
        <input
          type="text"
          className="h-8 rounded-md border border-slate-200 bg-slate-50 px-3 text-sm focus:border-indigo-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100"
          value={Array.isArray(value) ? value.join(", ") : (value ?? "")}
          onChange={(e) => {
            const parts = e.target.value.split(",").map((s) => s.trim()).filter((s) => s.length > 0);
            onChange(parts);
          }}
          placeholder="comma-separated values"
          aria-label={`${label} (multiple values)`}
        />
      ) : (
        <input
          type={inputType}
          className="h-8 rounded-md border border-slate-200 bg-slate-50 px-3 text-sm focus:border-indigo-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100"
          value={typeof value === "string" ? value : (Array.isArray(value) ? value[0] ?? "" : "")}
          onChange={(e) => onChange(e.target.value)}
          placeholder={prompt.defaultValues[0] ?? ""}
          aria-label={label}
        />
      )}
    </div>
  );
}

function inputTypeFor(p: ReportPromptDefinition): string {
  switch (p.dataType) {
    case "xsdDate":         return "date";
    case "xsdDateTime":     return "datetime-local";
    case "xsdTime":         return "time";
    case "xsdInt":
    case "xsdLong":
    case "xsdShort":
    case "xsdByte":
    case "xsdUnsignedInt":
    case "xsdUnsignedLong":
    case "xsdUnsignedShort":
    case "xsdUnsignedByte":
    case "xsdDecimal":
    case "xsdFloat":
    case "xsdDouble":       return "number";
    default:                return "text";
  }
}
